import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeState {
  mode: ThemeMode
  resolvedTheme: 'light' | 'dark'
  primaryColor: string

  // Actions
  setMode: (mode: ThemeMode) => void
  toggleTheme: () => void
  setPrimaryColor: (color: string) => void
  initializeTheme: () => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      resolvedTheme: 'light',
      primaryColor: '#2563eb',

      setMode: (mode: ThemeMode) => {
        set({ mode })
        get().applyTheme()
      },

      toggleTheme: () => {
        const { mode } = get()
        const newMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light'
        set({ mode: newMode })
        get().applyTheme()
      },

      setPrimaryColor: (color: string) => {
        set({ primaryColor: color })
        document.documentElement.style.setProperty('--color-primary', color)
      },

      applyTheme: () => {
        const { mode } = get()
        let resolved: 'light' | 'dark'

        if (mode === 'system') {
          resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
        } else {
          resolved = mode
        }

        set({ resolvedTheme: resolved })

        // Apply to document
        document.documentElement.classList.remove('light', 'dark')
        document.documentElement.classList.add(resolved)

        // Update meta theme-color
        const metaThemeColor = document.querySelector('meta[name="theme-color"]')
        if (metaThemeColor) {
          metaThemeColor.setAttribute('content', resolved === 'dark' ? '#0a0a0a' : '#fafafa')
        }
      },

      initializeTheme: () => {
        const { applyTheme } = get()
        applyTheme()

        // Listen for system theme changes
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        mediaQuery.addEventListener('change', () => {
          const { mode } = get()
          if (mode === 'system') {
            applyTheme()
          }
        })

        // Apply primary color
        document.documentElement.style.setProperty('--color-primary', get().primaryColor)
      },
    }),
    {
      name: 'kaseb-theme',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        mode: state.mode,
        primaryColor: state.primaryColor,
      }),
    }
  )
)

// Hook for easy access
export const useTheme = () => {
  const { mode, resolvedTheme, primaryColor, setMode, toggleTheme, setPrimaryColor } = useThemeStore()

  return {
    mode,
    resolvedTheme,
    primaryColor,
    isDark: resolvedTheme === 'dark',
    setMode,
    toggleTheme,
    setPrimaryColor,
  }
}